"use client";

/**
 * Schedule Row
 * One schedule item as a list row: time on the left, then title, details and tinted label pills,
 * then a "Now" or "Starts in" pill and the builder-only delete button on the right. On narrow
 * widths the time sits above the title. Shared by the Agenda, Accordion and Carousel layouts.
 *
 * `surface` says what the row sits on: "card" (a white card, fixed grays) or "section" (straight on
 * the section background, so text follows the section's `--lp-text` colors and works on dark ones).
 */

import type React from "react";

import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import type { ViewerRegistrationKeys } from "../../../../_logic/utils/schedule-registration-match";
import { formatStartsIn } from "../../../../_logic/utils/schedule-utils";
import { formatScheduleItemTime } from "../../../../_logic/utils/schedule-display-format";
import { ItemDeleteButton } from "./ItemDeleteButton";
import { ScheduleItemPills } from "./ScheduleItemPills";
import { ScheduleLabelPill } from "./ScheduleLabelPill";

/** What a schedule row sits on. */
export type ScheduleRowSurface = "card" | "section";

/** Render context shared by every row in a layout (everything except the per-row item and live state). */
export interface ScheduleRowConfig {
  labels: ScheduleLabelRow[];
  primaryColor: string;
  onDelete?: (item: ScheduleItemRow) => void;
  /** When provided, the row is clickable (the public detail modal, or focusing the item in the builder). */
  onItemClick?: (item: ScheduleItemRow) => void;
  isPublic?: boolean;
  viewerRegistrations: ViewerRegistrationKeys | null;
  surface: ScheduleRowSurface;
}

interface ScheduleRowProps {
  /** Shared render context, the same for every row. */
  config: ScheduleRowConfig;
  item: ScheduleItemRow;
  /** When true, shows a "Now" pill (the item is in progress). */
  isLive?: boolean;
  /** Minutes until the item starts. When set and not live, shows a "Starts in" pill. */
  startsInMinutes?: number;
}

const SURFACE_TEXT: Record<ScheduleRowSurface, { time: string; title: string; meta: string; hover: string }> = {
  card: {
    time: "text-gray-800",
    title: "text-gray-900",
    meta: "text-gray-600",
    hover: "hover:bg-gray-50",
  },
  section: {
    time: "text-(--lp-text,#262626)",
    title: "text-(--lp-text,#171717)",
    meta: "text-(--lp-text-muted,#525252)",
    hover: "hover:bg-(--lp-surface-hover,#f9fafb)",
  },
};

// Builder canvas uses container queries (the canvas narrows to a phone width); the public page
// uses viewport breakpoints. Full class strings so Tailwind sees them.
const GRID_CLASS = {
  public: "grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[11rem_minmax(0,1fr)_auto]",
  builder: "grid-cols-[minmax(0,1fr)_auto] @sm:grid-cols-[11rem_minmax(0,1fr)_auto]",
};
const STATUS_CELL_CLASS = {
  public: "col-start-2 row-start-1 sm:col-start-3",
  builder: "col-start-2 row-start-1 @sm:col-start-3",
};
const DETAILS_CELL_CLASS = {
  public: "col-span-2 row-start-2 sm:col-span-1 sm:col-start-2 sm:row-start-1",
  builder: "col-span-2 row-start-2 @sm:col-span-1 @sm:col-start-2 @sm:row-start-1",
};

/**
 * Renders one schedule row.
 *
 * @param props - See ScheduleRowProps.
 * @returns The row element.
 */
export function ScheduleRow({ config, item, isLive = false, startsInMinutes }: ScheduleRowProps): React.ReactElement {
  const { labels, primaryColor, onDelete, onItemClick, isPublic = false, viewerRegistrations, surface } = config;
  const text = SURFACE_TEXT[surface];
  const mode = isPublic ? "public" : "builder";
  const details = [item.location, item.speaker, item.vendor].filter((value): value is string => Boolean(value));

  return (
    <div
      className={`group/item -mx-3 grid items-baseline gap-x-6 gap-y-1 rounded-lg px-3 py-4 ${GRID_CLASS[mode]} ${
        onItemClick
          ? `cursor-pointer touch-manipulation transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 ${text.hover}`
          : ""
      }`}
      onClick={onItemClick ? () => onItemClick(item) : undefined}
      role={onItemClick ? "button" : undefined}
      tabIndex={onItemClick ? 0 : undefined}
      onKeyDown={
        onItemClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onItemClick(item);
              }
            }
          : undefined
      }
    >
      <span className={`col-start-1 row-start-1 whitespace-nowrap text-base font-semibold tabular-nums ${text.time}`}>
        {formatScheduleItemTime(item)}
      </span>

      <div className={`flex min-w-0 flex-col gap-0.5 ${DETAILS_CELL_CLASS[mode]}`}>
        <p className={`wrap-break-word text-base font-semibold leading-snug ${text.title}`}>{item.title}</p>
        {details.map((detail) => (
          <p key={detail} className={`wrap-break-word text-sm font-medium ${text.meta}`}>
            {detail}
          </p>
        ))}
        <div className="mt-1.5 empty:hidden">
          <ScheduleItemPills item={item} labels={labels} viewerRegistrations={viewerRegistrations} />
        </div>
      </div>

      <div className={`flex items-center gap-1 self-center justify-self-end ${STATUS_CELL_CLASS[mode]}`}>
        {isLive ? (
          <ScheduleLabelPill color={primaryColor}>Now</ScheduleLabelPill>
        ) : (
          startsInMinutes != null && (
            <ScheduleLabelPill color={primaryColor}>Starts in {formatStartsIn(startsInMinutes)}</ScheduleLabelPill>
          )
        )}
        {onDelete && <ItemDeleteButton item={item} onDelete={onDelete} />}
      </div>
    </div>
  );
}
